import type { AdminRepository } from '@/domain/port/AdminRepository';
import type { AdminChapter } from '@/domain/model/AdminChapter';
import type { AdminCourse } from '@/domain/model/AdminCourse';
import type { AdminStats } from '@/domain/model/AdminStats';
import type { AdminSubcategory } from '@/domain/model/AdminSubcategory';
import type { AdminUser } from '@/domain/model/AdminUser';
import type { AdminUserProfile } from '@/domain/model/AdminUserProfile';
import type { Role } from '@/domain/model/Role';
import type { Category } from '@/domain/model/Category';
import type { CreateCategoryRequest } from '@/application/dto/CreateCategoryRequest';
import type { UpdateCategoryRequest } from '@/application/dto/UpdateCategoryRequest';
import type { CreateSubcategoryRequest } from '@/application/dto/CreateSubcategoryRequest';
import type { UpdateSubcategoryRequest } from '@/application/dto/UpdateSubcategoryRequest';
import type { CreateChapterRequest } from '@/application/dto/CreateChapterRequest';
import type { UpdateChapterRequest } from '@/application/dto/UpdateChapterRequest';
import type { CreateCourseRequest } from '@/application/dto/CreateCourseRequest';
import type { UpdateCourseRequest } from '@/application/dto/UpdateCourseRequest';
import type { CourseUploadFiles, NewCourseFiles } from '@/domain/model/CourseUploadFiles';

export class AdminService {
  private readonly adminRepository: AdminRepository;

  constructor(adminRepository: AdminRepository) {
    this.adminRepository = adminRepository;
  }

  async getStats(): Promise<AdminStats> {
    return this.adminRepository.getStats();
  }

  async getUsers(search?: string): Promise<AdminUser[]> {
    return this.adminRepository.getUsers(search);
  }

  async getUserProfile(id: string): Promise<AdminUserProfile> {
    return this.adminRepository.getUserProfile(id);
  }

  async changeUserRole(id: string, role: Role): Promise<AdminUser> {
    return this.adminRepository.changeUserRole(id, role);
  }

  async deleteUser(id: string): Promise<void> {
    return this.adminRepository.deleteUser(id);
  }

  async getCategories(): Promise<Category[]> {
    return this.adminRepository.getCategories();
  }

  async createCategory(data: CreateCategoryRequest): Promise<Category> {
    return this.adminRepository.createCategory(data);
  }

  async updateCategory(id: string, data: UpdateCategoryRequest): Promise<Category> {
    return this.adminRepository.updateCategory(id, data);
  }

  async deleteCategory(id: string): Promise<void> {
    return this.adminRepository.deleteCategory(id);
  }

  async getSubcategories(categoryId?: string): Promise<AdminSubcategory[]> {
    return this.adminRepository.getSubcategories(categoryId);
  }

  async createSubcategory(data: CreateSubcategoryRequest): Promise<void> {
    return this.adminRepository.createSubcategory(data);
  }

  async updateSubcategory(id: string, data: UpdateSubcategoryRequest): Promise<void> {
    return this.adminRepository.updateSubcategory(id, data);
  }

  async deleteSubcategory(id: string): Promise<void> {
    return this.adminRepository.deleteSubcategory(id);
  }

  async getChapters(subcategoryId?: string): Promise<AdminChapter[]> {
    return this.adminRepository.getChapters(subcategoryId);
  }

  async createChapter(data: CreateChapterRequest): Promise<void> {
    return this.adminRepository.createChapter(data);
  }

  async updateChapter(id: string, data: UpdateChapterRequest): Promise<void> {
    return this.adminRepository.updateChapter(id, data);
  }

  async deleteChapter(id: string): Promise<void> {
    return this.adminRepository.deleteChapter(id);
  }

  async getCourses(chapterId?: string): Promise<AdminCourse[]> {
    return this.adminRepository.getCourses(chapterId);
  }

  async createCourse(
    data: CreateCourseRequest,
    files: NewCourseFiles,
    onProgress?: (progress: number) => void,
  ): Promise<void> {
    return this.adminRepository.createCourse(data, files, onProgress);
  }

  async updateCourse(
    id: string,
    data: UpdateCourseRequest,
    files: CourseUploadFiles,
    onProgress?: (progress: number) => void,
  ): Promise<void> {
    return this.adminRepository.updateCourse(id, data, files, onProgress);
  }

  async deleteCourse(id: string): Promise<void> {
    return this.adminRepository.deleteCourse(id);
  }

  async removeCourseDocument(courseId: string, documentId: string): Promise<void> {
    return this.adminRepository.removeCourseDocument(courseId, documentId);
  }
}
